import React, { useState, useRef, useEffect, memo, useCallback } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
   GLOBAL CSS  (marquee + avatar ring only)
───────────────────────────────────────────── */
const BASE_STYLES = `
  @keyframes marquee    { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes avatarRing { 0%,100%{box-shadow:0 0 0 0 rgba(168,85,247,.5)} 70%{box-shadow:0 0 0 10px rgba(168,85,247,0)} }
  @keyframes dotBlink   { 0%,100%{opacity:1} 50%{opacity:.2} }
  @keyframes borderRun  { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
`;

/* ═══════════════════════════════════════════════
   TECH MARQUEE
═══════════════════════════════════════════════ */
const TECHS_BASE = ["React", "Framer Motion", "Node.js", "MongoDB", "Next.js",
  "Tailwind", "TypeScript", "Figma", "Redux", "Express"];
const TECHS = [...TECHS_BASE, ...TECHS_BASE];

export const TechMarquee = memo(function TechMarquee() {
  return (
    <div className="tech-marquee-wrap" style={{ padding: "80px 0", overflow: "hidden", position: "relative" }}>
      <style>{BASE_STYLES}</style>
      <div className="tech-marquee-fade-left" style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 140,
        background: "linear-gradient(to right,#050508,transparent)", zIndex: 2, pointerEvents: "none"
      }} />
      <div className="tech-marquee-fade-right" style={{
        position: "absolute", right: 0, top: 0, bottom: 0, width: 140,
        background: "linear-gradient(to left,#050508,transparent)", zIndex: 2, pointerEvents: "none"
      }} />
      <div className="tech-marquee-track" style={{
        display: "flex", gap: 20, width: "max-content",
        animation: "marquee 30s linear infinite", willChange: "transform"
      }}>
        {TECHS.map((t, i) => (
          <div key={i}
            onMouseEnter={e => { e.currentTarget.style.color = "#c084fc"; e.currentTarget.style.boxShadow = "0 0 20px rgba(124,58,237,0.35)"; e.currentTarget.style.borderColor = "rgba(124,58,237,0.4)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "rgba(241,240,245,0.5)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "rgba(124,58,237,0.15)"; }}
            style={{
              padding: "10px 24px", borderRadius: 100, whiteSpace: "nowrap", cursor: "default",
              background: "rgba(255,255,255,0.03)", border: "1px solid rgba(124,58,237,0.15)",
              fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500,
              color: "rgba(241,240,245,0.5)", transition: "all 0.25s ease"
            }}>{t}
          </div>
        ))}
      </div>
    </div>
  );
});

/* ═══════════════════════════════════════════════
   TESTIMONIALS  (shake-free CSS grid stack)
═══════════════════════════════════════════════ */
const TESTIMONIALS = [
  {
    name: "Arjun Mehta", role: "Founder, DarkStack", avatar: "A", color: "#7c3aed",
    quote: "NyxLabs delivered something beyond a website — they handed us a digital experience. The animations feel alive and our conversion rate jumped 40%."
  },
  {
    name: "Priya Sharma", role: "CEO, VoidTech", avatar: "P", color: "#a855f7",
    quote: "Worked with many dev studios. None come close. The attention to dark aesthetics, the animations, the performance — it's another level entirely."
  },
  {
    name: "Raza Khan", role: "Product Lead, Neon Works", avatar: "R", color: "#e879f9",
    quote: "Our landing page went from boring to breathtaking. The NyxLabs team understood exactly what we needed before we even explained it."
  },
];

const TCard = memo(function TCard({ t, active }) {
  return (
    <div style={{
      opacity: active ? 1 : 0, pointerEvents: active ? "auto" : "none",
      transition: "opacity 0.45s ease", gridArea: "1/1",
      padding: "48px 52px", borderRadius: 24,
      background: "rgba(255,255,255,0.025)", border: `1px solid ${t.color}30`,
      boxShadow: active ? `0 0 60px ${t.color}12` : "none",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: -8, left: 24, fontSize: 140, lineHeight: 1,
        color: `${t.color}09`, fontFamily: "'Syne',sans-serif", fontWeight: 800,
        userSelect: "none", pointerEvents: "none", zIndex: 0
      }}>"</div>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg,transparent,${t.color}80,transparent)`,
        opacity: active ? 1 : 0, transition: "opacity 0.45s ease"
      }} />
      <p style={{
        fontFamily: "'DM Sans',sans-serif", fontSize: 19, fontWeight: 300,
        color: "rgba(241,240,245,0.82)", lineHeight: 1.85, fontStyle: "italic",
        marginBottom: 36, position: "relative", zIndex: 1
      }}>"{t.quote}"</p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, position: "relative", zIndex: 1 }}>
        <div style={{
          width: 52, height: 52, borderRadius: "50%", flexShrink: 0,
          background: `linear-gradient(135deg,${t.color},#e879f9)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 20, color: "#fff",
          animation: active ? "avatarRing 2.2s ease-out infinite" : "none",
          willChange: "box-shadow"
        }}>{t.avatar}</div>
        <div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#f1f0f5", marginBottom: 3 }}>{t.name}</div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "rgba(241,240,245,0.45)" }}>{t.role}</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 3 }}>
          {[...Array(5)].map((_, i) => (
            <span key={i} style={{ fontSize: 13, color: "#a855f7", textShadow: "0 0 8px rgba(168,85,247,0.8)" }}>★</span>
          ))}
        </div>
      </div>
    </div>
  );
});

export function Testimonials() {
  const [active, setActive] = useState(0);
  const cardsRef = useRef(null);
  const touchStartRef = useRef(0);
  const mouseDownRef = useRef(false);

  const handleNext = useCallback(() => {
    setActive(prev => (prev + 1) % TESTIMONIALS.length);
  }, []);

  const handlePrev = useCallback(() => {
    setActive(prev => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  }, []);

  // Touch events for mobile
  const handleTouchStart = useCallback(e => {
    touchStartRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(e => {
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStartRef.current - touchEnd;
    if (Math.abs(diff) > 50) {
      diff > 0 ? handleNext() : handlePrev();
    }
  }, [handleNext, handlePrev]);

  // Mouse events for drag
  const handleMouseDown = useCallback(e => {
    mouseDownRef.current = true;
    touchStartRef.current = e.clientX;
    if (cardsRef.current) cardsRef.current.style.cursor = "grabbing";
  }, []);

  const handleMouseUp = useCallback(e => {
    if (!mouseDownRef.current) return;
    mouseDownRef.current = false;
    if (cardsRef.current) cardsRef.current.style.cursor = "grab";

    const diff = touchStartRef.current - e.clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? handleNext() : handlePrev();
    }
  }, [handleNext, handlePrev]);

  const handleMouseMove = useCallback(e => {
    if (!mouseDownRef.current) return;
  }, []);

  return (
    <div className="testimonials-wrap" style={{ padding: "80px 60px", maxWidth: 1200, margin: "0 auto" }}>
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.7 }}
        style={{ textAlign: "center", marginBottom: 56 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px",
          borderRadius: 100, background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.25)",
          marginBottom: 20
        }}>
          <span style={{
            fontSize: 11, fontFamily: "'DM Sans',sans-serif", fontWeight: 500,
            color: "#c084fc", letterSpacing: "0.1em", textTransform: "uppercase"
          }}>Testimonials</span>
        </div>
        <h2 style={{
          fontFamily: "'Syne',sans-serif", fontWeight: 800,
          fontSize: "clamp(32px,5vw,52px)", lineHeight: 1.1, color: "#f1f0f5"
        }}>
          Voices from the{" "}
          <span style={{
            background: "linear-gradient(135deg,#a855f7,#e879f9)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
          }}>
            Void
          </span>
        </h2>
      </motion.div>
      <div
        ref={cardsRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
        style={{ display: "grid", marginBottom: 32, cursor: "grab", userSelect: "none", WebkitUserSelect: "none" }}
      >
        {TESTIMONIALS.map((t, i) => <TCard key={t.name} t={t} active={i === active} />)}
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
        {TESTIMONIALS.map((_, i) => (
          <button key={i} onClick={() => setActive(i)} style={{
            width: active === i ? 28 : 8, height: 8, borderRadius: 100, border: "none", cursor: "pointer",
            background: active === i ? "#a855f7" : "rgba(255,255,255,0.15)",
            boxShadow: active === i ? "0 0 14px rgba(168,85,247,0.7)" : "none",
            transition: "all 0.3s ease"
          }} />
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   THREE.JS CANVAS  —  Constellation / Neural Network
   Dots connected by glowing lines that pulse and morph.
   Zero floating, zero translateX. Pure depth.
═══════════════════════════════════════════════════════════════ */
function ConstellationCanvas() {
  const canvasRef = useRef(null);
  const stateRef = useRef({});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let raf;
    (async () => {
      let THREE;
      try { THREE = await import("three"); } catch { return; }

      const W = canvas.offsetWidth || 900;
      const H = canvas.offsetHeight || 480;

      const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setSize(W, H);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
      renderer.setClearColor(0x000000, 0);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 200);
      camera.position.set(0, 0, 28);

      /* ── Node positions ── */
      const NODE_COUNT = 60;
      const positions = [];
      for (let i = 0; i < NODE_COUNT; i++) {
        positions.push({
          x: (Math.random() - 0.5) * 36,
          y: (Math.random() - 0.5) * 20,
          z: (Math.random() - 0.5) * 12,
          vx: (Math.random() - 0.5) * 0.008,
          vy: (Math.random() - 0.5) * 0.008,
        });
      }

      /* ── Nodes (points) ── */
      const ptGeo = new THREE.BufferGeometry();
      const ptPos = new Float32Array(NODE_COUNT * 3);
      const ptCol = new Float32Array(NODE_COUNT * 3);
      const palette = [[0.49, 0.23, 0.93], [0.66, 0.33, 0.97], [0.75, 0.52, 0.99], [0.91, 0.47, 0.98], [0.96, 0.25, 0.37]];
      for (let i = 0; i < NODE_COUNT; i++) {
        const c = palette[i % palette.length];
        ptCol[i * 3] = c[0]; ptCol[i * 3 + 1] = c[1]; ptCol[i * 3 + 2] = c[2];
      }
      ptGeo.setAttribute("position", new THREE.BufferAttribute(ptPos, 3));
      ptGeo.setAttribute("color", new THREE.BufferAttribute(ptCol, 3));
      const ptMat = new THREE.PointsMaterial({
        size: 0.18, vertexColors: true,
        transparent: true, opacity: 0.85, sizeAttenuation: true
      });
      const points = new THREE.Points(ptGeo, ptMat);
      scene.add(points);

      /* ── Lines between close nodes ── */
      const MAX_LINES = 120;
      const linePos = new Float32Array(MAX_LINES * 2 * 3);
      const lineGeo = new THREE.BufferGeometry();
      lineGeo.setAttribute("position", new THREE.BufferAttribute(linePos, 3));
      const lineMat = new THREE.LineSegments(lineGeo,
        new THREE.LineBasicMaterial({ color: 0xa855f7, transparent: true, opacity: 0.18 }));
      scene.add(lineMat);

      /* Mouse parallax */
      const mouse = { x: 0, y: 0 };
      const onMove = (e) => {
        const r = canvas.getBoundingClientRect();
        mouse.x = ((e.clientX - r.left) / r.width - 0.5);
        mouse.y = -((e.clientY - r.top) / r.height - 0.5);
      };
      window.addEventListener("mousemove", onMove, { passive: true });

      /* Resize */
      const onResize = () => {
        const w = canvas.offsetWidth, h = canvas.offsetHeight;
        renderer.setSize(w, h); camera.aspect = w / h; camera.updateProjectionMatrix();
      };
      window.addEventListener("resize", onResize, { passive: true });

      let t = 0;
      const CONN_DIST = 9; // max distance to draw a line

      const tick = () => {
        raf = requestAnimationFrame(tick);
        t += 0.004;

        /* Drift nodes */
        for (let i = 0; i < NODE_COUNT; i++) {
          const p = positions[i];
          p.x += p.vx; p.y += p.vy;
          if (Math.abs(p.x) > 18) { p.vx *= -1; }
          if (Math.abs(p.y) > 10) { p.vy *= -1; }
          /* Gentle z-breathe */
          p.z = Math.sin(t * 0.7 + i) * 4;
          ptPos[i * 3] = p.x; ptPos[i * 3 + 1] = p.y; ptPos[i * 3 + 2] = p.z;
        }
        ptGeo.attributes.position.needsUpdate = true;

        /* Rebuild lines */
        let lIdx = 0;
        for (let i = 0; i < NODE_COUNT && lIdx < MAX_LINES; i++) {
          for (let j = i + 1; j < NODE_COUNT && lIdx < MAX_LINES; j++) {
            const dx = positions[i].x - positions[j].x;
            const dy = positions[i].y - positions[j].y;
            const dz = positions[i].z - positions[j].z;
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (dist < CONN_DIST) {
              linePos[lIdx * 6] = positions[i].x; linePos[lIdx * 6 + 1] = positions[i].y; linePos[lIdx * 6 + 2] = positions[i].z;
              linePos[lIdx * 6 + 3] = positions[j].x; linePos[lIdx * 6 + 4] = positions[j].y; linePos[lIdx * 6 + 5] = positions[j].z;
              lIdx++;
            }
          }
        }
        lineGeo.attributes.position.needsUpdate = true;
        lineGeo.setDrawRange(0, lIdx * 2);
        lineMat.material.opacity = 0.14 + Math.sin(t) * 0.06;

        /* Subtle camera parallax from mouse — NO translate, pure rotation */
        camera.rotation.x += (mouse.y * 0.04 - camera.rotation.x) * 0.05;
        camera.rotation.y += (mouse.x * 0.06 - camera.rotation.y) * 0.05;

        renderer.render(scene, camera);
      };
      tick();

      stateRef.current = { renderer, raf, onMove, onResize };
    })();

    return () => {
      const { renderer, raf, onMove, onResize } = stateRef.current;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      if (renderer) renderer.dispose();
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: "absolute", inset: 0, width: "100%", height: "100%",
      borderRadius: "inherit", pointerEvents: "none",
    }} />
  );
}

/* ═══════════════════════════════════════════════════════════════
   WORD-BY-WORD REVEAL  —  no translateX, no translateY bounce
   Each word fades + scales from 0.92→1 with blur clear.
   Clean, cinematic, zero float.
═══════════════════════════════════════════════════════════════ */
function WordReveal({ text, gradient, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const words = text.split(" ");

  return (
    <span ref={ref} style={{
      display: "inline",
      /* Pre-reserve layout space so nothing shifts */
    }}>
      {words.map((word, i) => (
        <React.Fragment key={i}>
          <motion.span
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{
              duration: 0.55,
              delay: delay + i * 0.065,
              ease: "easeOut",
            }}
            style={{
              display: "inline",
              /* NO scale, NO blur, NO transform — prevents layout jitter */
              ...(gradient ? {
                background: gradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              } : {}),
            }}>
            {word}
          </motion.span>
          {/* Preserve space between words as a separate static node */}
          {i < words.length - 1 && (
            <span style={{ display: "inline" }}>{" "}</span>
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   COUNTER  — number rolls up from 0
═══════════════════════════════════════════════════════════════ */
function RollingNumber({ target, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const unsub = count.on("change", v => setDisplay(Math.round(v).toString()));
    return unsub;
  }, [count]);

  useEffect(() => {
    if (inView && target !== "∞") {
      animate(count, parseInt(target), { duration: 1.4, ease: [0.16, 1, 0.3, 1] });
    }
  }, [inView, target, count]);

  return (
    <span ref={ref} style={{
      fontFamily: "'Syne',sans-serif", fontWeight: 800,
      fontSize: "clamp(26px,3.5vw,38px)",
      background: "linear-gradient(135deg,#f1f0f5,#c084fc)",
      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
    }}>
      {target === "∞" ? "∞" : (display + suffix)}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DRAW-ON SVG LINES  — paths draw themselves in on scroll
═══════════════════════════════════════════════════════════════ */
function DrawLines() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      <svg width="100%" height="100%" viewBox="0 0 900 480"
        preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0 }}>
        {/* Top-left arc */}
        <motion.path d="M0,80 Q200,20 450,60 T900,40"
          fill="none" stroke="url(#lg1)" strokeWidth="0.8" opacity="0.4"
          initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ duration: 2.2, delay: 0.3, ease: "easeInOut" }} />
        {/* Bottom sweep */}
        <motion.path d="M0,400 Q300,460 600,410 T900,440"
          fill="none" stroke="url(#lg2)" strokeWidth="0.8" opacity="0.35"
          initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ duration: 2.4, delay: 0.6, ease: "easeInOut" }} />
        {/* Diagonal cross */}
        <motion.path d="M100,480 Q450,240 800,0"
          fill="none" stroke="url(#lg3)" strokeWidth="0.6" opacity="0.2"
          initial={{ pathLength: 0 }} animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ duration: 2, delay: 0.9, ease: "easeInOut" }} />
        <defs>
          <linearGradient id="lg1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0" />
            <stop offset="50%" stopColor="#e879f9" stopOpacity="1" />
            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lg2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0" />
            <stop offset="50%" stopColor="#7c3aed" stopOpacity="1" />
            <stop offset="100%" stopColor="#e879f9" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lg3" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#c084fc" stopOpacity="0" />
            <stop offset="50%" stopColor="#e879f9" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAGNETIC BUTTON  —  rAF throttled, spring ease
═══════════════════════════════════════════════════════════════ */
function MagBtn({ label, onClick, primary }) {
  const ref = useRef(null);
  const raf = useRef(null);

  const onMove = useCallback((e) => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      if (!ref.current) { raf.current = null; return; }
      const r = ref.current.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) * 0.18;
      const dy = (e.clientY - (r.top + r.height / 2)) * 0.18;
      ref.current.style.transform = `translate(${dx}px,${dy}px)`;
      raf.current = null;
    });
  }, []);

  return (
    <motion.button ref={ref} onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={() => { if (ref.current) ref.current.style.transform = "translate(0,0)"; }}
      whileHover={primary
        ? { scale: 1.04, boxShadow: "0 0 60px rgba(124,58,237,0.72)" }
        : { scale: 1.03, borderColor: "rgba(168,85,247,0.6)", color: "#c084fc" }}
      whileTap={{ scale: 0.96 }}
      style={{
        padding: "15px 42px", borderRadius: 14, cursor: "pointer", fontWeight: 700, fontSize: 15,
        fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.03em",
        background: primary ? "linear-gradient(135deg,#7c3aed,#a855f7)" : "transparent",
        border: primary ? "none" : "1px solid rgba(124,58,237,0.38)",
        color: primary ? "#fff" : "rgba(241,240,245,0.75)",
        boxShadow: primary ? "0 0 40px rgba(124,58,237,0.5)" : "none",
        willChange: "transform",
        transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1),border-color 0.25s,color 0.25s,box-shadow 0.28s",
      }}>
      {label}
    </motion.button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   GLOW CTA  —  full rebuild
   ✓ Three.js constellation (not wormhole — no forward motion)
   ✓ SVG draw-on lines
   ✓ Word-blur-reveal heading (no translateX / translateY)
   ✓ Rolling number counters
   ✓ Magnetic buttons
   ✓ Zero floating effects
═══════════════════════════════════════════════════════════════ */
export function GlowCTA() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const STATS = [
    { value: "100", suffix: "+", label: "Projects Shipped" },
    { value: "50", suffix: "+", label: "Happy Clients" },
    { value: "3", suffix: "+", label: "Years in Dark" },
    { value: "∞", suffix: "", label: "Lines of Code" },
  ];

  return (
    <div style={{ padding: "80px 60px", maxWidth: 1200, margin: "0 auto" }}>
      <motion.div ref={ref}
        className="glow-cta-card"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
        style={{
          borderRadius: 32, textAlign: "center",
          border: "1px solid rgba(124,58,237,0.2)",
          position: "relative", overflow: "hidden",
          minHeight: 480, padding: "88px 48px 72px",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "linear-gradient(160deg,rgba(6,3,14,0.98),rgba(10,5,20,0.98))",
        }}>

        {/* Three.js constellation background */}
        <ConstellationCanvas />

        {/* SVG draw-on decorative lines */}
        <DrawLines />

        {/* Flowing border lines top/bottom */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 1, pointerEvents: "none",
          background: "linear-gradient(90deg,transparent,#7c3aed,#e879f9,#f43f5e,transparent)",
          backgroundSize: "200% 100%", animation: "borderRun 4s linear infinite", willChange: "background-position"
        }} />
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 1, pointerEvents: "none",
          background: "linear-gradient(90deg,transparent,#f43f5e,#e879f9,#7c3aed,transparent)",
          backgroundSize: "200% 100%", animation: "borderRun 4s linear infinite reverse", willChange: "background-position"
        }} />

        {/* Vignette edges */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(ellipse at 50% 50%,transparent 40%,rgba(6,3,14,0.7) 100%)"
        }} />

        {/* Content */}
        <div style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 600, margin: "0 auto" }}>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.55, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ marginBottom: 32 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "6px 20px", borderRadius: 100,
              background: "rgba(124,58,237,0.14)", border: "1px solid rgba(124,58,237,0.35)"
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%", background: "#a855f7",
                boxShadow: "0 0 8px #a855f7", display: "block",
                animation: "dotBlink 1.6s ease-in-out infinite", willChange: "opacity"
              }} />
              <span style={{
                fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600,
                color: "#c084fc", letterSpacing: "0.14em", textTransform: "uppercase"
              }}>
                Ready to Build
              </span>
            </div>
          </motion.div>

          {/* ── WORD BLUR REVEAL HEADING ── */}
          <h2 style={{
            fontFamily: "'Syne',sans-serif", fontWeight: 800,
            fontSize: "clamp(30px,5.5vw,64px)", lineHeight: 1.08,
            letterSpacing: "-0.02em", margin: "0 0 24px",
          }}>
            <span style={{ display: "block", color: "#f1f0f5", marginBottom: 6 }}>
              <WordReveal text="Let's Build Something" delay={0.3} />
            </span>
            <span style={{ display: "block" }}>
              <WordReveal
                text="Unforgettable"
                gradient="linear-gradient(135deg,#a855f7 0%,#e879f9 55%,#f43f5e 100%)"
                delay={0.52}
              />
            </span>
          </h2>

          {/* ── SUBTITLE — single block fade, zero jitter ── */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.7, delay: 0.72, ease: "easeOut" }}
            style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: 17, fontWeight: 300,
              color: "rgba(241,240,245,0.5)", maxWidth: 460, margin: "0 auto 48px", lineHeight: 1.82
            }}>
            Your next project deserves more than a template.
            Let's engineer something that lives in the dark and shines.
          </motion.p>

          {/* ── BUTTONS ── */}
          <motion.div
            className="glow-cta-actions"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.9 }}
            style={{
              display: "flex", gap: 16, justifyContent: "center",
              flexWrap: "wrap", marginBottom: 56
            }}>
            <div className="glow-cta-btn"><MagBtn label="Start a Project →" onClick={() => navigate("/contact")} primary /></div>
            <div className="glow-cta-btn"><MagBtn label="View Services" onClick={() => navigate("/services")} /></div>
          </motion.div>

          {/* ── DIVIDER ── */}
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={inView ? { scaleX: 1, opacity: 1 } : {}}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            style={{
              height: 1, marginBottom: 36,
              background: "linear-gradient(90deg,transparent,rgba(124,58,237,0.35),rgba(232,121,249,0.35),transparent)",
              transformOrigin: "center"
            }} />

          {/* ── ROLLING STAT COUNTERS ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.2 }}
            style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 0 }}>
            {STATS.map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 1.25 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  style={{ textAlign: "center", padding: "0 22px" }}>
                  <RollingNumber target={s.value} suffix={s.suffix} />
                  <div style={{
                    fontFamily: "'DM Sans',sans-serif", fontSize: 12,
                    color: "rgba(241,240,245,0.35)", letterSpacing: "0.05em", marginTop: 4
                  }}>
                    {s.label}
                  </div>
                </motion.div>
                {i < arr.length - 1 && (
                  <div style={{ width: 1, background: "rgba(124,58,237,0.18)", margin: "6px 0" }} />
                )}
              </React.Fragment>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <style>{`
        @media (max-width: 992px) {
          .tech-marquee-wrap { padding: 64px 0 !important; }
          .testimonials-wrap { padding: 72px 30px !important; }
        }
        @media (max-width: 768px) {
          .tech-marquee-wrap { padding: 52px 0 !important; }
          .tech-marquee-fade-left, .tech-marquee-fade-right { width: 56px !important; }
          .tech-marquee-track { gap: 12px !important; }
          .tech-marquee-track > div { padding: 8px 16px !important; font-size: 12px !important; }
          .testimonials-wrap { padding: 60px 20px !important; }
          .testimonials-card-stack > div { padding: 28px 20px !important; }
          .glow-cta-wrap { padding: 60px 20px !important; }
          .glow-cta-card { padding: 56px 22px 52px !important; min-height: 0 !important; }
          .glow-cta-actions { gap: 12px !important; }
          .glow-cta-btn button { width: 100%; }
        }
        @media (max-width: 480px) {
          .glow-cta-card { border-radius: 20px !important; }
        }
      `}</style>
    </div>
  );
}
